import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import apiFetch from '@wordpress/api-fetch';
import { __ } from '@wordpress/i18n';
import ElectionHubWidget from 'components/electionHubWidget';

// Styled components.
import styles from './styles';

class App extends React.Component {
  /**
   * Define initial state.
   * @type {object}
   */
  state = {
    data: {},
    loading: true,
  };

  /**
   * Define PropTypes for this component.
   * @type {object}
   */
  static propTypes = {
    dataUrl: PropTypes.string.isRequired,
    widgetId: PropTypes.string.isRequired,
  };

  /**
   * Constructor. Fires on component creation.
   * @param {object} props - Props for this object.
   */
  constructor(props) {
    super(props);
    this.fetchData = this.fetchData.bind(this);
  }

  /**
   * The componentDidMount lifecycle hook. Fires when the component has mounted.
   */
  componentDidMount() {
    this.fetchData();
  }

  /**
   * The componentWillUnmount lifecycle hook. Fires when the component will unmount.
   */
  componentWillUnmount() {
    const {
      updater,
    } = this.state;

    // If a timeout has been established for fetching data, clear it.
    if (updater) {
      clearTimeout(updater);
    }
  }

  /**
   * An action to fetch new data and to schedule the next data fetch.
   */
  fetchData() {
    const {
      dataUrl,
    } = this.props;

    const {
      lastUpdated: lastUpdatedCurrent,
      loading,
    } = this.state;

    // Fetch the data for this instance of the widget using the provided URL.
    apiFetch({
      path: dataUrl,
    })
      .then((data) => {
        const {
          last_updated: lastUpdatedNew = '',
        } = data;

        // Build a new state object initialized with a timeout to fetch more data.
        const newState = {
          updater: setTimeout(this.fetchData, 60000),
        };

        // If there is a new last updated date, set it, along with the new data.
        if (lastUpdatedCurrent !== lastUpdatedNew) {
          newState.lastUpdated = lastUpdatedNew;
          newState.data = data;
        }

        // If the loading flag is set, unset it.
        if (loading) {
          newState.loading = false;
        }

        this.setState(newState);
      })
      .catch((error) => {
        console.error(error); // eslint-disable-line no-console
      });
  }

  /**
   * Renders the app.
   * @returns {object} - JSX for this component.
   */
  render() {
    const {
      data = {},
      loading = true,
    } = this.state;

    const { widgetId } = this.props;

    const {
      Loading,
    } = styles;

    // Handle loading state.
    if (loading) {
      return (
        <Loading
          role="alert"
          aria-live="polite"
        >
          {__('Loading...', 'election-hub')}
        </Loading>
      );
    }

    return (
      <ElectionHubWidget widgetId={widgetId} {...data} />
    );
  }
}

// Activate the app on every embed on the page.
Array.from(document.querySelectorAll('.election-hub-widget'))
  .forEach((element) => {
    ReactDOM.render(
      <App
        dataUrl={element.getAttribute('data-url')}
        widgetId={element.getAttribute('data-widget-id')}
      />,
      element
    );
  });
